// kult-b2b-form.js — Custom Element <kult-b2b-form> für sections/kult-b2b-form.liquid.
// Aufgaben: HTML5-Validierung mit deutschen Fehlermeldungen (keine Browser-Tooltips),
// Live-Zusammenfassung ({Label}- und {Label*N}-Platzhalter -> aktueller/berechneter
// Feldwert, debounced),
// URL-Vorbelegung, Datumsregeln (Mindestdatum, Kurzfristig-Hinweis, gesperrte
// Wochentage) und Personenzahl-Schwellwert-Hinweis.
// Versand (seit Okt. 2026): nie über den Browser (Shopifys POST /contact lief je nach Bot-Schutz auf eine weiße
// Prüfseite), sondern ohne Seitenwechsel über die App: window.KultB2BLead.submit(form) aus assets/kult-b2b-lead.js.
// Erfolg: Formular bekommt .is-sent, [data-form-success] wird sichtbar und fokussiert, Ereignis "kult:b2b:sent".
// Fehler: Text in [data-form-errors] ([data-form-errors-text]), Eingaben bleiben stehen. Kein Doppel-Submit. Keine eigene Scroll-Animation für das
// Formular (siehe assets/kult-motion.js für data-reveal).
//
// Übergabe aus Rechner, Sortiment-Karten und Format-Karten (global, einmal registriert):
//  (a) Klick auf a[href^="#"], dessen Hash ein "?" enthält (z. B. #kontakt?ma=25&kg=6):
//      preventDefault, Anker und Parameter trennen, Parameter über die url_map der
//      passenden Formular-Instanz in deren Felder schreiben (Instanz = Section/Anker
//      passt zum Ankernamen; fehlt der Anker auf der Seite, die einzige Instanz), sanft
//      zum Anker scrollen (prefers-reduced-motion beachtet), Fokus auf die Überschrift
//      bzw. das erste Feld, History per replaceState auf #<anker>. Zeigt der Anker auf
//      eine Section ohne <kult-b2b-form>, bleibt der Link unberührt.
//  (b) Dieselbe Logik beim Laden und bei "hashchange", wenn location.hash ein "?" enthält.
//  Vor dem Absenden: Event "kult:b2b:before-submit" auf dem Formular (bubbles) mit
//      detail { form, data: FormData, waitUntil(promise) } für Vorab-Aufgaben anderer Werkzeuge.
//  (c) Custom-Event "kult:b2b:prefill" auf document/window mit detail {anchor, values, silent}
//      (silent: true = nur Werte eintragen, ohne Scrollen und Fokus; so hält der Rechner das
//      Formular laufend aktuell). Beim Start sendet dieses Skript "kult:b2b:sync-request".
//      (values = { param: wert }), löst dasselbe aus.
// Vorbelegung über Klick/Event/Hash überschreibt vorhandene Werte (bewusste Aktion);
// Parameter aus der Query-Zeichenkette (?param=x) setzen nur leere Felder. Select/Radio
// wählen die passende Option (ohne Groß-/Kleinschreibung), sonst wird das Textfeld gesetzt.
// Ein Parameter, der nicht in der url_map steht, wird einem Feld mit gleichem Label
// zugeordnet (ohne Groß-/Kleinschreibung). Ohne passendes Feld wird "sorte" als Zeile
// "Gewünschte Sorte: …" an das erste Textarea angehängt.
// Parameter des Bürokaffee-Rechners (assets/kult-b2b-calculator.js):
//   ma = Mitarbeitende, kg = Menge je Monat (kg), rhythmus = monatlich|14-tägig|wöchentlich,
//   zubereiter = Vollautomat|Siebträger|Filter, sorte = Titel der vorgeschlagenen Sorte.
// Sortiment-Karten senden sorte=<Titel>; Format-Karten (kult-b2b-packages) z. B. art=<Format>.
(function () {
  if (customElements.get('kult-b2b-form')) return;

  const MSG_REQUIRED = 'Bitte füllen Sie dieses Feld aus.';
  const MSG_EMAIL = 'Bitte geben Sie eine gültige E-Mail-Adresse ein.';
  const MSG_DATE_INVALID = 'Bitte geben Sie ein gültiges Datum ein.';
  const MSG_INVALID = 'Bitte geben Sie einen gültigen Wert ein.';
  const MSG_RANGE = 'Bitte geben Sie einen Wert zwischen {min} und {max} ein.';
  const MSG_SEND_FAILED = 'Ihre Anfrage konnte gerade nicht gesendet werden. Bitte versuchen Sie es gleich noch einmal oder schreiben Sie uns eine E-Mail.';

  class KultB2BForm extends HTMLElement {
    connectedCallback() {
      // Nur einmal einrichten: Der Konfigurator kann das Formular im DOM verschieben (Schritt „Ihre Kontaktdaten“)
      if (this.__kultInit) return;
      this.form = this.querySelector('form');
      if (!this.form) return;
      this.__kultInit = true;
      this.summaryEl = this.querySelector('[data-form-summary]');
      this.summaryTemplate = this.dataset.summaryTemplate || '';
      this.summaryCalcSuffix = this.dataset.summaryCalcSuffix || '';
      this.storageKey = 'kult-b2b-form:' + (this.form.id || 'default');

      this.bindValidation();
      this.bindSummary();
      this.bindPageshow();
      this.bindDateRules();
      this.bindPeopleThreshold();

      this.applyUrlParams();
      this.refreshDateFields();
      this.refreshPeopleHint();
      this.updateSummary();
    }

    fieldWrap(field) {
      return field.closest('.kult-b2b-form__field');
    }

    setFieldError(field, message) {
      const wrap = this.fieldWrap(field);
      if (!wrap) return;
      wrap.classList.toggle('has-error', !!message);
      field.setAttribute('aria-invalid', message ? 'true' : 'false');
      const errorEl = wrap.querySelector('[data-field-error]');
      if (!errorEl) return;
      const textEl = errorEl.querySelector('[data-field-error-text]');
      if (message) {
        if (textEl) textEl.textContent = message;
        errorEl.hidden = false;
      } else {
        errorEl.hidden = true;
      }
    }

    messageFor(field) {
      if (field.validity.valueMissing) return MSG_REQUIRED;
      if (field.validity.customError) return field.dataset.customError || MSG_REQUIRED;
      if (field.validity.typeMismatch && field.type === 'email') return MSG_EMAIL;
      if (field.validity.badInput) return field.type === 'date' ? MSG_DATE_INVALID : MSG_INVALID;
      if (field.validity.rangeUnderflow || field.validity.rangeOverflow) {
        const isDate = field.type === 'date';
        const min = field.min ? (isDate ? field.min.split('-').reverse().join('.') : field.min) : '';
        const max = field.max ? (isDate ? field.max.split('-').reverse().join('.') : field.max) : '';
        if (min && max) return MSG_RANGE.replace('{min}', min).replace('{max}', max);
        if (min) return 'Bitte geben Sie einen Wert ab ' + min + ' ein.';
        if (max) return 'Bitte geben Sie einen Wert bis ' + max + ' ein.';
      }
      if (field.validity.typeMismatch) return MSG_INVALID;
      return MSG_INVALID;
    }

    bindValidation() {
      const fields = this.form.querySelectorAll('input, select, textarea');
      fields.forEach((field) => {
        field.addEventListener('invalid', (event) => {
          event.preventDefault();
          this.setFieldError(field, this.messageFor(field));
        });
        field.addEventListener('input', () => {
          if (field.validity.valid) this.setFieldError(field, '');
        });
        field.addEventListener('change', () => {
          if (field.validity.valid) this.setFieldError(field, '');
        });
      });

      this.form.addEventListener('submit', (event) => {
        // Versand über die App, nie über den Browser: kein Seitenwechsel, keine weiße Seite
        event.preventDefault();
        if (this.submitting) return;
        this.refreshDateFields();
        if (!this.form.checkValidity()) { this.showInvalid(); return; }
        this.send();
      });
    }

    // Vorab-Aufgaben anderer Werkzeuge (z. B. Logo an die App senden, Konfiguration ins Formular schreiben) laufen über
    // das Ereignis "kult:b2b:before-submit" (detail { form, data, waitUntil(promise) }), höchstens 12 Sekunden, Fehler
    // halten nie auf. Danach geht die Anfrage über window.KultB2BLead.submit an die App.
    send() {
      const btn = this.form.querySelector('[data-form-submit]');
      const setBusy = (on) => {
        this.submitting = on;
        if (btn) { btn.disabled = on; if (on) btn.setAttribute('aria-busy', 'true'); else btn.removeAttribute('aria-busy'); }
        if (on) this.form.setAttribute('aria-busy', 'true'); else this.form.removeAttribute('aria-busy');
      };
      setBusy(true);
      this.showSendError('');
      const pending = this.collectPreTasks();
      const timeout = new Promise((resolve) => window.setTimeout(resolve, 12000));
      const pre = Promise.all(pending.map((p) => Promise.resolve(p).catch(() => undefined)));
      Promise.race([pre, timeout]).then(() => {
        const lead = window.KultB2BLead;
        return lead && typeof lead.submit === 'function' ? lead.submit(this.form) : { ok: false, error: '' };
      }).then((result) => {
        setBusy(false);
        if (result && result.ok) { this.showSuccess(); return; }
        this.showSendError((result && result.error) || MSG_SEND_FAILED);
      });
    }

    showSuccess() {
      const success = this.form.querySelector('[data-form-success]');
      this.form.classList.add('is-sent');
      if (success) {
        success.hidden = false;
        window.requestAnimationFrame(() => {
          success.focus({ preventScroll: true });
          success.scrollIntoView({ block: 'center' });
        });
      }
      try { window.sessionStorage.removeItem(this.storageKey); } catch (e) { /* ohne Speicher nichts zu löschen */ }
      this.dispatchEvent(new CustomEvent('kult:b2b:sent', { bubbles: true, detail: { form: this.form } }));
    }

    showSendError(text) {
      const box = this.form.querySelector('[data-form-errors]');
      if (!box) return;
      const target = box.querySelector('[data-form-errors-text]') || box;
      target.textContent = text || '';
      box.hidden = !text;
      if (text) box.scrollIntoView({ block: 'center' });
    }

    showInvalid() {
      const invalidFields = Array.from(this.form.querySelectorAll('input, select, textarea')).filter((f) => !f.validity.valid);
      invalidFields.forEach((f) => this.setFieldError(f, this.messageFor(f)));
      if (invalidFields[0]) invalidFields[0].focus();
    }

    collectPreTasks() {
      const pending = [];
      this.form.dispatchEvent(new CustomEvent('kult:b2b:before-submit', {
        bubbles: true,
        detail: { form: this.form, data: new FormData(this.form), waitUntil: (p) => { if (p && typeof p.then === 'function') pending.push(p); } },
      }));
      return pending;
    }

    bindPageshow() {
      // bfcache: Browser kann die Seite nach "Zurück" aus dem Cache statt neu zu laden
      // wiederherstellen — Submit-Button und Sperre müssen dann zurückgesetzt werden.
      window.addEventListener('pageshow', (event) => {
        if (!event.persisted) return;
        this.submitting = false;
        const submitBtn = this.form.querySelector('[data-form-submit]');
        if (submitBtn) submitBtn.disabled = false;
      });
    }

    debounce(fn, wait) {
      let timer;
      return (...args) => {
        window.clearTimeout(timer);
        timer = window.setTimeout(() => fn.apply(this, args), wait);
      };
    }

    bindSummary() {
      if (!this.summaryEl || !this.summaryTemplate) return;
      const debounced = this.debounce(() => this.updateSummary(), 300);
      this.form.addEventListener('input', debounced);
      this.form.addEventListener('change', debounced);
    }

    valueForKey(key) {
      const wrap = this.form.querySelector('[data-summary-key="' + CSS.escape(key) + '"]');
      if (!wrap) return '';
      const radio = wrap.querySelector('input[type="radio"]:checked');
      if (radio) return radio.value;
      if (wrap.querySelector('input[type="radio"]')) return '';
      const checkbox = wrap.querySelector('input[type="checkbox"]');
      if (checkbox) return checkbox.checked ? (checkbox.value || 'Ja') : '';
      const select = wrap.querySelector('select');
      if (select) return select.value;
      const input = wrap.querySelector('input, textarea');
      if (!input) return '';
      if (input.type === 'date' && input.value) {
        const parts = input.value.split('-');
        if (parts.length === 3) return parts[2] + '.' + parts[1] + '.' + parts[0];
      }
      return input.value;
    }

    updateSummary() {
      if (!this.summaryEl || !this.summaryTemplate) return;
      const filled = this.summaryTemplate.replace(/\{([^}]+)\}/g, (match, rawKey) => {
        const key = rawKey.trim();
        const calc = key.match(/^(.+?)\*(\d+)$/);
        if (calc) {
          const raw = this.valueForKey(calc[1].trim());
          const num = raw ? parseFloat(raw.replace(',', '.')) : NaN;
          if (!raw || Number.isNaN(num)) return '…';
          const result = Math.round(num * parseInt(calc[2], 10));
          const suffix = this.summaryCalcSuffix ? ' ' + this.summaryCalcSuffix : '';
          return 'ca. ' + result + suffix;
        }
        const value = this.valueForKey(key);
        return value && value.length ? value : '…';
      });
      this.summaryEl.textContent = filled;
    }

    // ---------- URL-Vorbelegung (§4.7): ?param=x und #anker?param=x ----------
    parseUrlMap(raw) {
      const map = {};
      (raw || '').split(',').forEach((pair) => {
        const idx = pair.indexOf(':');
        if (idx === -1) return;
        const key = pair.slice(0, idx).trim();
        const label = pair.slice(idx + 1).trim();
        if (key && label) map[key] = label;
      });
      return map;
    }

    collectParams() {
      // Hash-Parameter (#anker?param=x) behandelt die globale Übergabe (siehe Dateikopf)
      return new URLSearchParams(window.location.search);
    }

    fieldWrapForParam(param, map) {
      if (map[param]) {
        const wrap = this.form.querySelector('[data-summary-key="' + CSS.escape(map[param]) + '"]');
        if (wrap) return wrap;
      }
      const wanted = param.trim().toLowerCase();
      return Array.from(this.form.querySelectorAll('[data-summary-key]'))
        .find((w) => (w.getAttribute('data-summary-key') || '').trim().toLowerCase() === wanted) || null;
    }

    notifyChange(field) {
      ['input', 'change'].forEach((type) => field.dispatchEvent(new Event(type, { bubbles: true })));
    }

    // Schreibt Werte { param: wert } in die Felder; force = vorhandene Werte überschreiben.
    prefill(values, force) {
      const map = this.parseUrlMap(this.dataset.urlMap || '');
      let changed = false;
      Object.keys(values || {}).forEach((param) => {
        const value = values[param] == null ? '' : String(values[param]).trim();
        if (!value) {
          // Leerer Wert löscht nur das Feld "logo" (Logo in der Unternehmensmischung entfernt), sonst bleibt alles stehen
          if (force && param.toLowerCase() === 'logo' && values[param] === '') {
            const logoWrap = this.fieldWrapForParam(param, map);
            const logoInput = logoWrap && logoWrap.querySelector('input:not([type="radio"]):not([type="checkbox"])');
            if (logoInput && logoInput.value) { logoInput.value = ''; this.notifyChange(logoInput); changed = true; }
          }
          return;
        }
        const wrap = this.fieldWrapForParam(param, map);
        if (wrap) {
          if (this.setFieldValue(wrap, value, force)) changed = true;
        } else if (param.toLowerCase() === 'sorte') {
          const area = this.form.querySelector('textarea');
          const line = 'Gewünschte Sorte: ' + value;
          if (area && area.value.indexOf(line) === -1) {
            area.value = area.value ? line + '\n' + area.value : line;
            this.notifyChange(area);
            changed = true;
          }
        }
      });
      if (changed) this.updateSummary();
      return changed;
    }

    setFieldValue(wrap, value, force) {
      const wanted = value.toLowerCase();
      const radios = wrap.querySelectorAll('input[type="radio"]');
      if (radios.length) {
        if (!force && wrap.querySelector('input[type="radio"]:checked')) return false;
        const match = Array.from(radios).find((r) => r.value.trim().toLowerCase() === wanted);
        if (!match) return false;
        match.checked = true;
        this.notifyChange(match);
        return true;
      }
      const select = wrap.querySelector('select');
      if (select) {
        if (!force && select.value) return false;
        const option = Array.from(select.options).find((o) => o.value && (
          o.value.trim().toLowerCase() === wanted || o.textContent.trim().toLowerCase() === wanted));
        if (!option) return false;
        select.value = option.value;
        this.notifyChange(select);
        return true;
      }
      const input = wrap.querySelector('input:not([type="radio"]):not([type="checkbox"]), textarea');
      if (input) {
        if (!force && input.value) return false;
        input.value = value;
        this.notifyChange(input);
        return true;
      }
      return false;
    }

    applyUrlParams() {
      const params = this.collectParams();
      const values = {};
      params.forEach((value, key) => { values[key] = value; });
      this.prefill(values, false);
    }

    // Fokus nach der Übergabe: Überschrift der Section, sonst erstes Feld
    focusAfterPrefill() {
      const section = this.closest('section') || this;
      const heading = section.querySelector('h2, h3');
      if (heading) {
        if (!heading.hasAttribute('tabindex')) heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
        return;
      }
      const first = this.form.querySelector('input:not([type="hidden"]), select, textarea');
      if (first) first.focus({ preventScroll: true });
    }

    // ---------- Datumsregeln: Kurzfristig-Hinweis + gesperrte Wochentage ----------
    bindDateRules() {
      this.dateFields = Array.from(this.form.querySelectorAll('input[type="date"]')).map((field) => {
        const wrap = this.fieldWrap(field);
        const shortNoticeDays = parseInt(field.dataset.shortNoticeDays || '', 10);
        const blockedWeekdays = (field.dataset.blockedWeekdays || '')
          .split(',').map((d) => d.trim()).filter((d) => d !== '').map(Number);
        const descriptor = {
          field,
          shortNoticeDays: Number.isNaN(shortNoticeDays) ? 0 : shortNoticeDays,
          blockedWeekdays,
          shortNoticeEl: wrap ? wrap.querySelector('[data-date-shortnotice]') : null,
          blockedEl: wrap ? wrap.querySelector('[data-date-blocked]') : null
        };
        const evaluate = () => this.evaluateDateField(descriptor);
        field.addEventListener('change', evaluate);
        field.addEventListener('input', evaluate);
        return descriptor;
      });
    }

    evaluateDateField(descriptor) {
      const { field, shortNoticeDays, blockedWeekdays, shortNoticeEl, blockedEl } = descriptor;
      if (!field.value) {
        if (shortNoticeEl) shortNoticeEl.hidden = true;
        if (blockedEl) blockedEl.hidden = true;
        field.setCustomValidity('');
        return;
      }
      const selected = new Date(field.value + 'T00:00:00');
      if (Number.isNaN(selected.getTime())) return;
      if (shortNoticeEl && shortNoticeDays > 0) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const diffDays = Math.round((selected - today) / 86400000);
        shortNoticeEl.hidden = !(diffDays >= 0 && diffDays < shortNoticeDays);
      }
      if (blockedEl && blockedWeekdays.length) {
        const isBlocked = blockedWeekdays.indexOf(selected.getDay()) !== -1;
        blockedEl.hidden = !isBlocked;
        if (isBlocked) {
          const textEl = blockedEl.querySelector('span:last-child');
          field.dataset.customError = textEl ? textEl.textContent : MSG_REQUIRED;
          field.setCustomValidity('blocked');
        } else {
          field.setCustomValidity('');
        }
      }
    }

    refreshDateFields() {
      (this.dateFields || []).forEach((descriptor) => this.evaluateDateField(descriptor));
    }

    // ---------- Personenzahl-Schwellwert ----------
    bindPeopleThreshold() {
      const hintEl = this.form.querySelector('[data-people-threshold]');
      if (!hintEl) return;
      const threshold = parseInt(hintEl.dataset.peopleThreshold, 10);
      if (Number.isNaN(threshold) || threshold <= 0) return;
      const wrap = this.fieldWrap(hintEl);
      const input = wrap ? wrap.querySelector('input') : null;
      if (!input) return;
      this.peopleThresholdRule = { hintEl, threshold, input };
      const evaluate = () => this.refreshPeopleHint();
      input.addEventListener('input', evaluate);
      input.addEventListener('change', evaluate);
    }

    refreshPeopleHint() {
      const rule = this.peopleThresholdRule;
      if (!rule) return;
      const value = parseInt(rule.input.value, 10);
      rule.hintEl.hidden = !(!Number.isNaN(value) && value >= rule.threshold);
    }

  }

  customElements.define('kult-b2b-form', KultB2BForm);

  // ---------- Übergabe über Hash-Links, Hash beim Laden und Event (siehe Dateikopf) ----------
  const SCROLL_OFFSET = 96;

  function formsOnPage() {
    return Array.from(document.querySelectorAll('kult-b2b-form')).filter((el) => el.form);
  }

  function resolveInstance(anchor) {
    const forms = formsOnPage();
    const target = anchor ? document.getElementById(anchor) : null;
    if (target) {
      const match = forms.find((f) => target === f || target.contains(f)) ||
        forms.find((f) => f.contains(target));
      return match ? { form: match, target } : null;
    }
    return forms.length === 1 ? { form: forms[0], target: forms[0].closest('section') || forms[0] } : null;
  }

  function parseHash(hash) {
    const raw = (hash || '').replace(/^#/, '');
    const q = raw.indexOf('?');
    if (q === -1) return null;
    const values = {};
    new URLSearchParams(raw.slice(q + 1)).forEach((value, key) => { values[key] = value; });
    return { anchor: raw.slice(0, q), values };
  }

  function scrollContainer() {
    const pw = document.querySelector('.page-wrapper');
    if (!pw) return null;
    const cs = window.getComputedStyle(pw);
    return /(auto|scroll)/.test(cs.overflowY) && pw.scrollHeight > pw.clientHeight + 1 ? pw : null;
  }
  function scrollTopOf(sc) { return sc ? sc.scrollTop : window.pageYOffset; }
  function scrollTo(sc, top, behavior) { (sc || window).scrollTo({ top, behavior }); }

  function handOver(anchor, values, options) {
    const hit = resolveInstance(anchor);
    if (!hit) return false;
    hit.form.prefill(values, true);
    if (options && options.silent) return true; // nur Werte übernehmen (laufender Abgleich mit dem Rechner)
    const reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    // Ab 990 px scrollt .page-wrapper statt window (html/body overflow hidden, base.css) – Cowork-Test 06.10.2026, Abw. 4
    const sc = scrollContainer();
    const top = hit.target.getBoundingClientRect().top + scrollTopOf(sc) - SCROLL_OFFSET;
    scrollTo(sc, Math.max(0, top), reduce ? 'auto' : 'smooth');
    // Fokus erst nach dem Scrollen: Safari bricht ein laufendes sanftes Scrollen ab, sobald ein Element
    // den Fokus bekommt (dann „passiert nichts“). scrollend, wo vorhanden; sonst Zeitpuffer.
    let focused = false;
    const focusNow = () => {
      if (focused) return;
      focused = true;
      (sc || window).removeEventListener('scrollend', focusNow);
      const rest = hit.target.getBoundingClientRect().top - SCROLL_OFFSET;
      if (Math.abs(rest) > 8) scrollTo(sc, Math.max(0, rest + scrollTopOf(sc)), 'auto');
      hit.form.focusAfterPrefill();
    };
    if (reduce) focusNow();
    else {
      if ('onscrollend' in window) (sc || window).addEventListener('scrollend', focusNow);
      window.setTimeout(focusNow, 'onscrollend' in window ? 2500 : 1100);
    }
    if (!options || options.history !== false) {
      const id = hit.target.id || anchor;
      if (id && window.history && window.history.replaceState) {
        window.history.replaceState(null, '', window.location.pathname + window.location.search + '#' + id);
      }
    }
    return true;
  }

  document.addEventListener('click', (event) => {
    if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    const link = event.target.closest && event.target.closest('a[href^="#"]');
    if (!link) return;
    const parsed = parseHash(link.getAttribute('href'));
    if (!parsed) return;
    if (handOver(parsed.anchor, parsed.values)) event.preventDefault();
  });

  function handleLocationHash() {
    const parsed = parseHash(window.location.hash);
    if (parsed) handOver(parsed.anchor, parsed.values);
  }

  window.addEventListener('hashchange', handleLocationHash);

  const onPrefillEvent = (event) => {
    if (!event || event.__kultB2bHandled) return;
    event.__kultB2bHandled = true;
    const detail = event.detail || {};
    handOver(String(detail.anchor || '').replace(/^#/, ''), detail.values || {}, { history: true, silent: detail.silent === true });
  };
  document.addEventListener('kult:b2b:prefill', onPrefillEvent);
  window.addEventListener('kult:b2b:prefill', onPrefillEvent);

  handleLocationHash();
  // Rechner und andere Werkzeuge, die vor diesem Skript gestartet sind, reichen ihren Stand nach
  document.dispatchEvent(new CustomEvent('kult:b2b:sync-request'));
})();
